"use client"

import * as React from "react"
import { motion, useAnimation } from "motion/react"
import type { HTMLAttributes, MouseEvent } from "react"
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react"

import { cn } from "@/lib/utils"

export interface RefreshCCWIconHandle {
  startAnimation: () => void
  stopAnimation: () => void
}

interface RefreshCCWIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number
}

const RefreshCCWIcon = forwardRef<RefreshCCWIconHandle, RefreshCCWIconProps>(
  ({ onMouseEnter, onMouseLeave, className, size = 28, ...props }, ref) => {
    const controls = useAnimation()
    const isControlledRef = useRef(false)

    useImperativeHandle(ref, () => {
      isControlledRef.current = true
      return {
        startAnimation: () => void controls.start("animate"),
        stopAnimation: () => void controls.start("normal"),
      }
    })

    const handleMouseEnter = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) onMouseEnter?.(e)
        else void controls.start("animate")
      },
      [controls, onMouseEnter]
    )

    const handleMouseLeave = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) onMouseLeave?.(e)
        else void controls.start("normal")
      },
      [controls, onMouseLeave]
    )

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.g
            animate={controls}
            transition={{ type: "spring", stiffness: 250, damping: 25 }}
            variants={{
              normal: { rotate: "0deg" },
              animate: { rotate: "-50deg" },
            }}
          >
            <path d="M3 2v6h6" />
            <path d="M21 12A9 9 0 0 0 6 5.3L3 8" />
            <path d="M21 22v-6h-6" />
            <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7" />
          </motion.g>
        </svg>
      </div>
    )
  }
)

RefreshCCWIcon.displayName = "RefreshCCWIcon"

export { RefreshCCWIcon }
